import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Button, Table, Row, Col, Alert, Collapse, Modal } from 'react-bootstrap'
import DataTable from '../_library/DataTable'
import { baseRequest } from '../../_common/core/http_services'
import { formatCurrency } from '../utils/currencyUtils'
import { formatDate } from '../../_common/core/validation/normalizers'
import { SEND_BULK_INVOICE, VOID_BULK_INVOICE } from '../../_common/redux/bulkInvoices/actions'
import { ROLES } from '../constants/roles'
import Clipboard from '../_library/Clipboard'

const BulkInvoiceDetails = ({ event, invoice, onClose, onRefresh, sendInvoice, voidInvoice, user, actionButtons }) => {
  const [isSending, setIsSending] = useState(false)
  const [isVoiding, setIsVoiding] = useState(false)
  const [showAuditLogs, setShowAuditLogs] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  // Auto-reload missing or invalid invoice data
  useEffect(() => {
    // Handle both direct and nested (attributes) invoice data structures
    const invoiceData = invoice?.attributes || invoice
    const isInvalidInvoice = !invoice || 
      !invoiceData?.id || 
      !invoiceData?.invoice_number ||
      invoiceData?.invoice_number === 'Unknown'
    
    console.log('BulkInvoiceDetails useEffect check:', { 
      hasInvoice: !!invoice, 
      hasId: !!invoiceData?.id, 
      invoiceNumber: invoiceData?.invoice_number,
      hasOnRefresh: !!onRefresh,
      isInvalidInvoice,
      fullInvoice: invoice // Log full object for debugging
    })
    
    if (isInvalidInvoice && onRefresh) {
      console.log('Triggering onRefresh due to invalid invoice data...')
      onRefresh()
    }
  }, [invoice, onRefresh])

  // Defensive check - ensure we always have valid invoice data
  // Handle both direct and nested (attributes) invoice data structures
  const invoiceData = invoice?.attributes || invoice
  const isInvalidInvoice = !invoice || 
    !invoiceData?.id || 
    !invoiceData?.invoice_number ||
    invoiceData?.invoice_number === 'Unknown'
    
  if (isInvalidInvoice) {
    return (
      <div className="bulk-invoice-details">
        <Alert variant="info">
          <i className="fa fa-spinner fa-spin" /> Loading invoice data...
          <div className="text-muted small mt-1">
            {!invoice ? 'No invoice data' : `Missing: ${!invoiceData.id ? 'ID ' : ''}${!invoiceData.invoice_number ? 'Number' : ''}`}
          </div>
          {onRefresh && (
            <div className="mt-2">
              <Button variant="outline-primary" size="sm" onClick={onRefresh}>
                <i className="fa fa-refresh" /> Retry Loading
              </Button>
            </div>
          )}
        </Alert>
      </div>
    )
  }

  const renderCustomerRows = () => {
    const rows = [
      <tr key="name"><td><strong>Name:</strong></td><td>{invoiceData.customer_name || 'N/A'}</td></tr>,
      <tr key="email"><td><strong>Email:</strong></td><td>{invoiceData.customer_email || 'N/A'}</td></tr>
    ]
    if (invoiceData.customer_company) {
      rows.push(<tr key="company"><td><strong>Company:</strong></td><td>{invoiceData.customer_company}</td></tr>)
    }
    return rows
  }

  const renderInvoiceRows = () => {
    const rows = [
      <tr key="created"><td><strong>Created:</strong></td><td>{invoiceData.created_at ? formatDate(invoiceData.created_at) : 'N/A'}</td></tr>,
      <tr key="due-date"><td><strong>Due Date:</strong></td><td>{invoiceData.due_date ? formatDate(invoiceData.due_date) : 'Not set'}</td></tr>
    ]
    if (invoiceData.paid_at) {
      rows.push(<tr key="paid"><td><strong>Paid:</strong></td><td>{formatDate(invoiceData.paid_at)}</td></tr>)
    }
    rows.push(<tr key="created-by"><td><strong>Created By:</strong></td><td>{invoiceData.created_by?.name || 'Unknown'}</td></tr>)
    return rows
  }

  const handleSendInvoice = async () => {
    if (!event?.id) {
      console.error('Event ID is required to send invoice')
      return
    }
    setIsSending(true)
    try {
      await sendInvoice(event.id, invoiceData.id)
      onRefresh()
    } catch (error) {
      console.error('Failed to send invoice:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleVoidInvoice = async () => {
    if (!event?.id) {
      console.error('Event ID is required to void invoice')
      return
    }
    setIsVoiding(true)
    try {
      await voidInvoice(event.id, invoiceData.id, voidReason)
      setShowVoidModal(false)
      setVoidReason('')
      onRefresh() // Refresh to show updated status
    } catch (error) {
      console.error('Failed to void invoice:', error)
      // You could add a toast notification here for better UX
    } finally {
      setIsVoiding(false)
    }
  }

  const canVoidInvoice = () => {
    return ['open', 'draft'].includes(invoiceData.status)
  }

  const getStatusBadge = status => {
    // If status is failed but invoice was recently created (within 2 minutes), show processing
    if (status === 'failed' && invoiceData.created_at) {
      const createdTime = new Date(invoiceData.created_at).getTime()
      const now = Date.now()
      const fiveMinutes = 5 * 60 * 1000
      
      if (now - createdTime < fiveMinutes) {
        return <span className="badge badge-warning">PROCESSING PAYMENT</span>
      }
    }
    
    const statusColors = {
      draft: 'warning',
      open: 'info',
      paid: 'success',
      void: 'danger',
      failed: 'danger',
      uncollectible: 'danger'
    }
    const safeStatus = status || 'unknown'
    return <span className={`badge badge-${statusColors[status] || 'secondary'}`}>{safeStatus.toUpperCase()}</span>
  }

  // Check if user can view audit logs
  const canViewAuditLogs = () => {
    if (!user || !user.tfRole) return false
    // Allow TTF staff, support roles, and developers to view audit logs
    return ROLES.is_ttf_staff(user.tfRole) || ROLES.is_tf_support(user.tfRole) || user.tfRole === ROLES.TF_DEVELOPER
  }

  const getActionBadge = action => {
    const actionColors = {
      created: 'success',
      updated: 'info',
      sent: 'primary',
      paid: 'success',
      voided: 'danger',
      delegation_created: 'warning'
    }
    return <span className={`badge badge-${actionColors[action] || 'secondary'}`}>{action.replace('_', ' ').toUpperCase()}</span>
  }

  // Wrap entire render in try-catch to prevent crashes
  try {
    return (
      <div className="bulk-invoice-details">
      <Row className="mb-4">
        <Col md={6}>
          <h5>Invoice {invoiceData.invoice_number || 'Unknown'}</h5>
          <div className="mb-2">{getStatusBadge(invoiceData.status)}</div>
          {invoiceData.stripe_invoice_id && (
            <div className="text-muted">
              Stripe ID: <code>{invoiceData.stripe_invoice_id}</code>
            </div>
          )}
        </Col>
        <Col md={6} className="text-right">
          <div className="mb-3" style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center'}}>
            {actionButtons}
            {invoiceData.payment_link && invoiceData.status !== 'void' && invoiceData.status !== 'paid' && (
              <Clipboard text={invoiceData.payment_link} tooltipText="Copy Payment Link">
                <Button variant="info" size="sm">
                  <i className="fa fa-copy" /> Copy Payment Link
                </Button>
              </Clipboard>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <h6>Customer Information</h6>
          <table className="table table-sm">
            <tbody>
              {renderCustomerRows()}
            </tbody>
          </table>
        </Col>
        <Col md={6}>
          <h6>Invoice Details</h6>
          <table className="table table-sm">
            <tbody>
              {renderInvoiceRows()}
            </tbody>
          </table>
        </Col>
      </Row>

      {invoiceData.description && (
        <div className="mb-3">
          <h6>Description</h6>
          <p>{invoiceData.description}</p>
        </div>
      )}

      {invoiceData.notes && (
        <div className="mb-3">
          <h6>Internal Notes</h6>
          <p className="text-muted">{invoiceData.notes}</p>
        </div>
      )}

      <h6>Invoice Items</h6>
      <DataTable
        thead={[
          'Type',
          'Item', 
          'Quantity',
          'Unit Price',
          'Total'
        ]}
        tbody={[
          ...(invoiceData.items || []).map(item => [
            item?.item_type || item?.type || 'Unknown',
            <div>
              {item?.item_name || item?.name || 'Unknown Item'}
              {item?.price_override_reason && (
                <div className="text-muted small">{item.price_override_reason}</div>
              )}
            </div>,
            item?.quantity || 0,
            <div>
              {formatCurrency(item?.unit_price || 0, invoiceData.currency || 'USD')}
              {item?.original_price && item.original_price !== item.unit_price && (
                <div className="text-muted small">
                  <s>{formatCurrency(item.original_price, invoiceData.currency || 'USD')}</s>
                </div>
              )}
            </div>,
            formatCurrency(item?.total_price || 0, invoiceData.currency || 'USD')
          ])
        ]}
        tfoot={(() => {
          // Calculate correct totals from items (frontend fix for backend bug)
          const calculatedSubtotal = (invoiceData.items || []).reduce((sum, item) => {
            return sum + parseFloat(item?.total_price || 0)
          }, 0)
          const calculatedTotal = calculatedSubtotal + parseFloat(invoiceData.tax_amount || 0)
          const currency = invoiceData.currency || 'USD'
          
          // Create footer rows array
          const footerRows = [
            ['', '', '', <strong>Subtotal:</strong>, <strong>{formatCurrency(calculatedSubtotal, currency)}</strong>]
          ]
          
          // Tax row (always shown) - get tax info from tax_class if available
          let taxName = 'Tax'
          let taxRate = 0
          
          if (invoiceData.tax_class && invoiceData.tax_class.rates && invoiceData.tax_class.rates.length > 0) {
            const firstRate = invoiceData.tax_class.rates[0]
            taxName = firstRate.tax_name || 'Tax'
            taxRate = parseFloat(firstRate.rate) || 0
          }
          
          const taxLabel = taxRate > 0 ? `${taxName} (${taxRate}%)` : taxName
          footerRows.push(['', '', '', <strong>{taxLabel}:</strong>, <strong>{formatCurrency(invoiceData.tax_amount || 0, currency)}</strong>])
          
          footerRows.push(['', '', '', <strong>Total:</strong>, <strong>{formatCurrency(calculatedTotal, currency)}</strong>])
          
          return footerRows
        })()}
      />

      {invoiceData.delegation_id && event?.id && (
        <Alert variant="info">
          This invoice has been paid and a delegation has been created.{' '}
          <a href={`/event/${event.id}/delegations/${invoiceData.delegation_id}`}>
            <i className="fa fa-users" /> View Delegation
          </a>
        </Alert>
      )}

      {/* Audit Logs Section - Role-based visibility */}
      {canViewAuditLogs() && invoiceData.audit_logs && Array.isArray(invoiceData.audit_logs) && invoiceData.audit_logs.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              <i className="fa fa-history" /> Activity Log
              <span className="badge badge-secondary ms-2">{invoiceData.audit_logs.length}</span>
            </h6>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowAuditLogs(!showAuditLogs)}
            >
              <i className={`fa fa-${showAuditLogs ? 'chevron-up' : 'chevron-down'}`} />
              {showAuditLogs ? ' Hide' : ' Show'} Activity
            </Button>
          </div>

          <Collapse in={showAuditLogs}>
            <div>
              <DataTable
                thead={[
                  'Date',
                  'Action', 
                  'User',
                  'Details'
                ]}
                tbody={invoiceData.audit_logs.map(log => [
                  <small className="text-muted">
                    {formatDate(log.created_at)}
                  </small>,
                  getActionBadge(log.action),
                  log.name ? (
                    <div>
                      <div className="fw-semibold">{log.name}</div>
                      <small className="text-muted">{log.email}</small>
                    </div>
                  ) : (
                    <span className="text-muted">System</span>
                  ),
                  log.details || <span className="text-muted">No additional details</span>
                ])}
              />
            </div>
          </Collapse>
        </div>
      )}

      {/* Debug info for authorized users */}
      {canViewAuditLogs() && (!invoiceData.audit_logs || invoiceData.audit_logs.length === 0) && (
        <div className="mt-4">
          <Alert variant="info">
            <i className="fa fa-info-circle" /> No activity logs available for this invoice.
          </Alert>
        </div>
      )}


      {/* Void Invoice Confirmation Modal */}
      <Modal show={showVoidModal} onHide={() => setShowVoidModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="fa fa-exclamation-triangle" /> 
            <strong> Warning:</strong> Voiding this invoice cannot be undone. 
            The invoice will be marked as void and cannot be paid.
          </Alert>
          <div>
            <div className="mb-3">
              <label className="form-label">Reason for voiding (optional):</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Enter reason for voiding this invoice..."
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowVoidModal(false)
              setVoidReason('')
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleVoidInvoice}
            disabled={isVoiding}
          >
            {isVoiding ? (
              <>
                <i className="fa fa-spinner fa-spin" /> Voiding...
              </>
            ) : (
              <>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    )
  } catch (error) {
    console.error('Error rendering BulkInvoiceDetails:', error)
    return (
      <div className="bulk-invoice-details">
        <Alert variant="danger">
          <i className="fa fa-exclamation-triangle" /> 
          <strong>Error displaying invoice details.</strong>
          <br />
          <small>Invoice ID: {invoiceData?.id || 'Unknown'} | Status: {invoiceData?.status || 'Unknown'}</small>
          <br />
          <Button variant="outline-secondary" size="sm" onClick={onRefresh} className="mt-2">
            <i className="fa fa-refresh" /> Try Refreshing
          </Button>
        </Alert>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  user: state.auth.user
})

const mapDispatchToProps = dispatch => ({
  sendInvoice: (eventId, invoiceId) => dispatch(SEND_BULK_INVOICE.RUN(eventId, invoiceId)),
  voidInvoice: (eventId, invoiceId, reason) => dispatch(VOID_BULK_INVOICE.RUN(eventId, invoiceId, reason))
})

export default connect(mapStateToProps, mapDispatchToProps)(BulkInvoiceDetails)